/**
 * Editor's Pick - 블로그 상세 페이지 공통 추천 포스트
 *
 * CMS editors_pick 카테고리의 `editors-pick` 슬러그 포스트를 데이터베이스로 사용한다.
 * 카테고리의 "첫 번째 글"이 아니라 슬러그로 특정한다 — 에디터픽 종류가 늘어나면
 * (시리즈 에디터픽 등) 같은 카테고리에 소스 포스트가 여러 개 생기기 때문이다.
 *
 * 본문에 언어(ko/en)별 포스트 정보와 광고(ads) 정보가 key: value 라인으로 나열되어
 * 있고, 포스트 슬러그들의 정보를 조회해 EditorsPickData 캐시를 만든다.
 * 광고는 슬러그 조회 없이 본문에 직접 입력된 link/title/image 를 그대로 사용한다.
 *
 * 포스트의 title·description·image 는 **오버라이드**다 — 적으면 그 값을 쓰고,
 * 비어 있거나 라인이 아예 없으면 원글에서 가져온다(title = WP 제목, image =
 * resolvePostListImage 체인). description 은 원글에 대응 필드가 없어 빈 문자열이다.
 *
 * 본문 포맷 예시 (라인 단위 key: value):
 *   ko-post-1-slug: slug-a
 *   ko-post-1-title: 캐러셀에 쓸 제목
 *   ko-post-1-description: 캐러셀에 쓸 설명
 *   ko-post-1-image:         ← 값이 비어 있으면 바로 다음 본문 이미지(figure img)의 src 사용
 *   ko-post-2-slug: slug-b
 *   ko-ads-link: /product
 *   ko-ads-title: 광고 제목
 *   ko-ads-image:            ← 광고도 같은 규칙
 *   en-post-1-slug: ...
 *   en-ads-link: ...
 */

import { CONTENT_CATEGORY_ID } from '@/constants/content-categoryId';
import { resolvePostListImage } from '@/utils/resolve-post-image';
import { CMS_IMAGE_MARKER_LINE, toCmsSourceLines } from '@/utils/sanitize';
import { fetchFromCache, FetchAPIOptions } from './api';
import { fetchFromWordpress } from './wordpress-api';
import { uploadWordpressCacheDataToS3 } from './wordpress-cache';

// ============================================
// Types
// ============================================

export interface EditorsPickItem {
  title: string;
  /** 소스 본문의 description 오버라이드 — 없으면 빈 문자열(원글에 대응 필드 없음) */
  description: string;
  category: string;
  link: string;
  image: string;
  createdAt: string;
}

/** 광고 — 슬러그 조회 없이 본문에 직접 입력된 값 사용 */
export interface EditorsPickAd {
  link: string;
  title: string;
  image: string;
}

export interface EditorsPickLangData {
  posts: EditorsPickItem[];
  ads: EditorsPickAd[]; // 0~1개 — link 가 없으면 미완성 입력으로 간주하고 제외
}

/** 언어별 캐시 데이터 — 사용처에서 locale 에 맞는 쪽을 사용 */
export interface EditorsPickData {
  ko: EditorsPickLangData;
  en: EditorsPickLangData;
}

type Lang = keyof EditorsPickData;

/**
 * 소스 본문에 적힌 포스트 1개.
 * slug 만 필수고 나머지는 오버라이드 — 미지정이면 빈 문자열이다.
 */
interface EditorsPickSourcePost {
  slug: string;
  title: string;
  description: string;
  image: string;
}

interface EditorsPickSourceLang {
  posts: EditorsPickSourcePost[]; // ko-post-{n}-* 의 n 오름차순
  ad: EditorsPickAd;
}

/** 본문 파싱 결과 */
export interface EditorsPickSource {
  ko: EditorsPickSourceLang;
  en: EditorsPickSourceLang;
}

/**
 * 시리즈 에디터픽 캐시 데이터 — 큐레이션된 시리즈 slug 만 담는다.
 *
 * 시리즈의 표시명·설명·이미지·멤버는 **여기 박제하지 않는다.** 사용처가 각 slug 로
 * `getSeries(slug)` 를 직접 호출해 그 시리즈 캐시를 읽고, 그러면 `getSeries-{slug}` 태그가
 * 사용처 페이지에 바인딩된다 — 시리즈 1건이 갱신되면 이 목록을 쓰는 페이지도 함께 stale 된다.
 * 값을 복사해 두면 그 연결이 끊겨 시리즈를 고쳐도 목록이 옛 내용을 보여준다.
 *
 * 로케일별로 다른 시리즈를 큐레이션할 수 있다 — 국문·영문에 보여줄 시리즈가 다르다.
 */
export interface SeriesEditorsPickData {
  ko: string[]; // {locale}-series-{n}-slug 의 n 오름차순
  en: string[];
}

/** EDITORS_PICK_ITEMS_QUERY 응답 노드 */
interface EditorsPickNode {
  slug: string;
  title: string;
  content?: string;
  featuredImage?: { node?: { mediaItemUrl?: string } };
  seo?: { opengraphImage?: { link?: string } };
  blog?: {
    categoryEn?: string[];
    categoryKo?: string[];
    date?: string;
    heroimage?: string;
  };
}

// ============================================
// 상수
// ============================================

/**
 * 캐시 폴더명이자 revalidateTag 태그 — 소스 포스트 slug 마다 별도 캐시를 갖는다.
 *
 * `content-revalidation.ts` 의 `PER_SLUG_CACHE_CONFIG.editors_pick.getItemCacheTag` 와
 * **정확히 같은 문자열**을 만들어야 한다. 어긋나면 웹훅이 무효화하는 태그와 페이지가
 * 바인딩한 태그가 달라져 갱신이 조용히 안 먹는다.
 */
const getEditorsPickCacheLabel = (slug: string) => `getEditorsPick-${slug}`;

/** 포스트 큐레이션 소스의 WP 슬러그 — editors_pick 카테고리 안에서 이 글을 특정한다 */
const EDITORS_PICK_SOURCE_SLUG = 'editors-pick';

/** 시리즈 큐레이션 소스의 WP 슬러그 — 같은 카테고리의 다른 글이다 */
const SERIES_EDITORS_PICK_SOURCE_SLUG = 'series-editors-pick';

/**
 * 소스 포스트 조회 — editors_pick 카테고리 + 슬러그로 특정 (본문에 포스트 목록)
 *
 * 슬러그 조회는 반드시 `nameIn`(= post_name__in) 이어야 한다. `name: $slug` 는
 * WP_Query 의 단일글 permalink 쇼트컷을 발동해 categoryId 필터를 무시하고,
 * `post(id:$slug, idType:SLUG)` 는 전역 슬러그 조회라 카테고리 스코프가 없다.
 * 카테고리 간 슬러그 중복이 실재하므로 둘 다 엉뚱한 포스트를 반환할 수 있다.
 */
const EDITORS_PICK_SOURCE_QUERY = `
query getEditorsPickSource($slugs: [String], $categoryId: Int!) {
  posts(first: 1, where: { nameIn: $slugs, categoryId: $categoryId, status: PUBLISH }) {
    nodes {
      content
    }
  }
}`;

/** 슬러그 목록으로 포스트 정보 일괄 조회 — content/seo 는 이미지 fallback 체인용 */
const EDITORS_PICK_ITEMS_QUERY = `
query getEditorsPickItems($slugs: [String]) {
  posts(first: 100, where: { nameIn: $slugs, status: PUBLISH }) {
    nodes {
      slug
      title
      content
      featuredImage {
        node {
          mediaItemUrl
        }
      }
      seo {
        opengraphImage {
          link
        }
      }
      blog {
        categoryEn
        categoryKo
        date
        heroimage
      }
    }
  }
}`;

// ============================================
// 본문 파싱
// ============================================

/**
 * 값이 빈 `image:` 라인이 예약한 "다음 본문 이미지"의 수신처.
 * 광고와 포스트 둘 다 같은 규칙을 쓰므로 대상을 구분해 기억한다.
 */
type PendingImage =
  | { kind: 'ad'; lang: Lang }
  | { kind: 'post'; lang: Lang; order: number };

/**
 * 소스 포스트 본문(HTML)에서 언어별 포스트 정보와 광고 정보를 추출한다.
 *
 * `{lang}-ads-image:` 와 `{lang}-post-{n}-image:` 는 값이 비어 있으면 바로 다음
 * 본문 이미지의 src 를 사용한다.
 *
 * 포스트는 라인이 흩어져 있어도(순서 무관) n 으로 묶고, 마지막에 n 오름차순으로
 * 정렬한다. slug 가 없는 n 은 버린다 — title 만 적고 slug 를 빼먹은 미완성 입력이
 * 포스트로 올라가면 조회 단계에서 빈 슬러그로 실패한다.
 *
 * WP 가 텍스처화한 엔티티(`&#8216;`·`&#038;` 등)는 `toCmsSourceLines` 가 라인 단위로
 * 이미 풀어 준다 — 여기서 값마다 다시 풀면 이중 디코딩이 된다.
 */
export const parseEditorsPickContent = (content: string): EditorsPickSource => {
  const lines = toCmsSourceLines(content);

  const posts: Record<Lang, Map<number, EditorsPickSourcePost>> = {
    ko: new Map(),
    en: new Map(),
  };
  const ads: Record<Lang, EditorsPickAd> = {
    ko: { link: '', title: '', image: '' },
    en: { link: '', title: '', image: '' },
  };

  let pendingImage: PendingImage | null = null;

  // 같은 n 의 라인들이 하나의 포스트로 모이도록 필요할 때 만들어 재사용한다
  const postAt = (lang: Lang, order: number): EditorsPickSourcePost => {
    const existing = posts[lang].get(order);
    if (existing) return existing;

    const created: EditorsPickSourcePost = {
      slug: '',
      title: '',
      description: '',
      image: '',
    };
    posts[lang].set(order, created);
    return created;
  };

  const assignPendingImage = (target: PendingImage, src: string) => {
    if (target.kind === 'ad') {
      ads[target.lang].image = src;
    } else {
      postAt(target.lang, target.order).image = src;
    }
  };

  for (const line of lines) {
    const imgMatch = line.match(CMS_IMAGE_MARKER_LINE);
    if (imgMatch) {
      const src = imgMatch[1];
      if (pendingImage && src) {
        assignPendingImage(pendingImage, src);
        pendingImage = null;
      }
      continue;
    }

    const postMatch = line.match(
      /^(ko|en)-post-(\d+)-(slug|title|description|image)\s*:\s*(.*)$/,
    );
    if (postMatch) {
      const [, lang, order, field, rawValue] = postMatch;
      const value = rawValue.trim();

      if (field === 'image' && !value) {
        pendingImage = {
          kind: 'post',
          lang: lang as Lang,
          order: Number(order),
        };
      } else if (value) {
        // 빈 값은 무시한다 — 앞서 채운 값을 지우지 않는다
        postAt(lang as Lang, Number(order))[
          field as keyof EditorsPickSourcePost
        ] = value;
      }
      continue;
    }

    const adMatch = line.match(/^(ko|en)-ads-(link|title|image)\s*:\s*(.*)$/);
    if (adMatch) {
      const [, lang, field, rawValue] = adMatch;
      const value = rawValue.trim();
      if (field === 'image' && !value) {
        pendingImage = { kind: 'ad', lang: lang as Lang };
      } else {
        ads[lang as Lang][field as keyof EditorsPickAd] = value;
      }
    }
  }

  const toSortedPosts = (map: Map<number, EditorsPickSourcePost>) =>
    Array.from(map.entries())
      .sort(([a], [b]) => a - b)
      .map(([, post]) => post)
      .filter(post => post.slug);

  return {
    ko: { posts: toSortedPosts(posts.ko), ad: ads.ko },
    en: { posts: toSortedPosts(posts.en), ad: ads.en },
  };
};

/**
 * 시리즈 에디터픽 소스 본문에서 큐레이션된 시리즈 slug 를 추출한다.
 *
 * 본문 포맷 (콜론 앞뒤 공백 허용 — CMS 에 `ko-series-1-slug : x` 형태로 입력돼 있다):
 *   ko-series-1-slug: gebra-use-guide
 *   ko-series-2-slug: sample-collection-guide
 *   en-series-1-slug: gebra-use-guide
 *
 * 로케일 접두사가 필수다 — 국문·영문에 다른 시리즈를 큐레이션할 수 있어야 한다.
 *
 * n 오름차순으로 정렬한다. 시리즈의 존재 여부는 **검증하지 않는다** — 시리즈 캐시가
 * 아직 없을 수도 있는데(시리즈 init 이 나중에 돌 수 있다) 여기서 실패하면 랜딩 빌드가
 * 시리즈 캐시 생성 순서에 묶인다. 없는 slug 는 사용처가 렌더 시점에 카드에서 제외한다.
 */
export const parseSeriesEditorsPickContent = (
  content: string,
): SeriesEditorsPickData => {
  const lines = toCmsSourceLines(content);
  const found: Record<Lang, { order: number; slug: string }[]> = {
    ko: [],
    en: [],
  };

  for (const line of lines) {
    const match = line.match(/^(ko|en)-series-(\d+)-slug\s*:\s*(.+)$/);
    if (!match) continue;

    const [, lang, order, slug] = match;
    const trimmed = slug.trim();
    if (!trimmed) continue;

    found[lang as Lang].push({ order: Number(order), slug: trimmed });
  }

  const sorted = (items: { order: number; slug: string }[]) =>
    items.sort((a, b) => a.order - b.order).map(item => item.slug);

  return { ko: sorted(found.ko), en: sorted(found.en) };
};

// ============================================
// 캐시 데이터 빌드
// ============================================

type BuildOptions = Pick<FetchAPIOptions, 'prodFilter'>;

/**
 * 소스 포스트 본문 조회 — slug 로 특정한다. 큐레이션 종류별 파서가 이 결과를 나눠 쓴다.
 *
 * 소스 포스트는 내부 설정용 데이터라 운영 배포(prod_deployed) 대상이 아니므로
 * 저자(authors)와 마찬가지로 prodFilter 를 적용하지 않는다.
 * (큐레이션된 포스트 조회는 prodFilter 적용 — 미배포 글 참조 방지)
 */
const fetchSourceContent = async (slug: string): Promise<string> => {
  const sourceData = await fetchFromWordpress(EDITORS_PICK_SOURCE_QUERY, {
    variables: {
      slugs: [slug],
      categoryId: CONTENT_CATEGORY_ID.editorsPick,
    },
  });
  const content: string | undefined = sourceData?.posts?.nodes?.[0]?.content;
  if (!content) {
    throw new Error(
      `editors_pick 카테고리에서 소스 포스트를 찾을 수 없습니다: ${slug}`,
    );
  }

  return content;
};

/** 포스트 큐레이션 소스 조회 + 본문 파싱 → 언어별 포스트·광고 정보 */
const fetchEditorsPickSource = async (): Promise<EditorsPickSource> => {
  const content = await fetchSourceContent(EDITORS_PICK_SOURCE_SLUG);

  return parseEditorsPickContent(content);
};

/**
 * 슬러그별 포스트 정보 일괄 조회 → slug 매핑
 * 슬러그가 하나라도 조회되지 않으면 잘못된 큐레이션이므로 throw 한다.
 */
const fetchEditorsPickNodes = async (
  allSlugs: string[],
  options?: BuildOptions,
): Promise<Map<string, EditorsPickNode>> => {
  const itemsData = await fetchFromWordpress(
    EDITORS_PICK_ITEMS_QUERY,
    { variables: { slugs: allSlugs } },
    options,
  );
  const nodes: EditorsPickNode[] = itemsData?.posts?.nodes || [];
  const nodeBySlug = new Map(nodes.map(node => [node.slug, node]));

  const missingSlugs = allSlugs.filter(slug => !nodeBySlug.has(slug));
  if (missingSlugs.length > 0) {
    throw new Error(
      `editors_pick 슬러그의 포스트를 찾을 수 없습니다: ${missingSlugs.join(', ')}`,
    );
  }

  return nodeBySlug;
};

/**
 * WordPress 에서 소스 포스트 + 슬러그별 포스트 정보를 조회해
 * EditorsPickData 를 만든다. (S3 업로드 없음 — 검증/dry-run 용으로도 사용)
 */
export const buildEditorsPickData = async (
  options?: BuildOptions,
): Promise<EditorsPickData> => {
  const source = await fetchEditorsPickSource();
  const allSlugs = Array.from(
    new Set([...source.ko.posts, ...source.en.posts].map(post => post.slug)),
  );
  if (allSlugs.length === 0) {
    throw new Error('editors_pick 본문에서 포스트 슬러그를 찾을 수 없습니다.');
  }

  const nodeBySlug = await fetchEditorsPickNodes(allSlugs, options);

  const toItem = (
    sourcePost: EditorsPickSourcePost,
    lang: Lang,
  ): EditorsPickItem => {
    const node = nodeBySlug.get(sourcePost.slug)!;
    const category =
      lang === 'ko' ? node.blog?.categoryKo?.[0] : node.blog?.categoryEn?.[0];

    return {
      title: sourcePost.title || node.title,
      // 원글에 대응 필드가 없어 소스에 적은 값만이 유일한 출처다
      description: sourcePost.description,
      category: category ?? '',
      link: `/blog/${sourcePost.slug}`,
      // 소스 오버라이드가 없으면 블로그 목록 페이지와 동일한 이미지 결정 로직
      // (heroImage → featuredImage → seoOpengraphImage → 본문 첫 이미지)
      image:
        sourcePost.image ||
        resolvePostListImage({
          heroImage: node.blog?.heroimage ?? '',
          featuredImage: node.featuredImage?.node?.mediaItemUrl ?? '',
          seoOpengraphImage: node.seo?.opengraphImage?.link,
          content: node.content,
        }) ||
        '',
      createdAt: node.blog?.date ?? '',
    };
  };

  // 광고는 link 가 있어야 유효 — 없으면 미완성 입력으로 간주하고 제외
  const toAds = (ad: EditorsPickAd): EditorsPickAd[] => (ad.link ? [ad] : []);

  return {
    ko: {
      posts: source.ko.posts.map(post => toItem(post, 'ko')),
      ads: toAds(source.ko.ad),
    },
    en: {
      posts: source.en.posts.map(post => toItem(post, 'en')),
      ads: toAds(source.en.ad),
    },
  };
};

/**
 * 시리즈 에디터픽 소스 조회 + 파싱 → 큐레이션된 시리즈 slug 목록.
 * (S3 업로드 없음 — 검증/dry-run 용으로도 사용)
 */
export const buildSeriesEditorsPickData =
  async (): Promise<SeriesEditorsPickData> => {
    const content = await fetchSourceContent(SERIES_EDITORS_PICK_SOURCE_SLUG);
    const data = parseSeriesEditorsPickContent(content);

    // 한쪽 로케일만 비는 건 허용한다(그 로케일에서 섹션이 안 보일 뿐) — 양쪽 다 비면 입력 실수다
    if (data.ko.length === 0 && data.en.length === 0) {
      throw new Error(
        'series-editors-pick 본문에서 시리즈 슬러그를 찾을 수 없습니다.',
      );
    }

    return data;
  };

// ============================================
// 조회 (빌드/런타임 + refresh)
// ============================================

/**
 * Editor's Pick 데이터 조회
 * - refresh: true → WordPress 조회 + 캐시 빌드 + S3 업로드 (스크립트/웹훅용, 실패 시 throw)
 * - refresh: false → S3 캐시 조회 (빌드/런타임용, 실패 시 null)
 */
export const getEditorsPick = async (
  options?: FetchAPIOptions,
): Promise<EditorsPickData | null> => {
  const refresh = options?.refresh ?? false;
  const label = getEditorsPickCacheLabel(EDITORS_PICK_SOURCE_SLUG);

  if (refresh) {
    const data = await buildEditorsPickData({
      prodFilter: options?.prodFilter,
    });
    await uploadWordpressCacheDataToS3(data, label);
    return data;
  }

  return fetchFromCache(label);
};

/**
 * 시리즈 에디터픽 데이터 조회
 * - refresh: true → WordPress 조회 + 캐시 빌드 + S3 업로드 (스크립트/웹훅용, 실패 시 throw)
 * - refresh: false → S3 캐시 조회 (빌드/런타임용, 실패 시 null)
 */
export const getSeriesEditorsPick = async (
  options?: FetchAPIOptions,
): Promise<SeriesEditorsPickData | null> => {
  const refresh = options?.refresh ?? false;
  const label = getEditorsPickCacheLabel(SERIES_EDITORS_PICK_SOURCE_SLUG);

  if (refresh) {
    const data = await buildSeriesEditorsPickData();
    await uploadWordpressCacheDataToS3(data, label);
    return data;
  }

  return fetchFromCache(label);
};

/**
 * 소스 slug → 그 캐시를 재생성하는 함수.
 *
 * 웹훅과 init 스크립트가 "저장된 글의 slug" 로 무엇을 갱신할지 찾는 유일한 출처다.
 * editors_pick 카테고리에 새 큐레이션 소스를 추가하면 여기에 등록해야 한다 —
 * 등록되지 않은 slug 는 `refreshEditorsPickSource` 가 명시적으로 실패시킨다.
 *
 * ⚠️ **plain object 가 아니라 Map 이어야 한다.** 조회 키가 CMS 에서 온 임의 문자열이라,
 * object 면 프로토타입 체인이 걸려든다 — `slug='constructor'` 는 `Object` 생성자를
 * 반환해 존재 검사를 통과하고, 호출해도 예외 없이 값을 돌려주므로 **캐시를 하나도 만들지
 * 않은 채 성공 응답이 나간다**(`toString` 도 같고 `__proto__` 는 원인 불명 500 이 된다).
 * Map 은 자기 엔트리만 보고 `get` 이 `undefined` 를 돌려주므로 아래 가드가 실제로 작동한다.
 */
export const EDITORS_PICK_SOURCES = new Map<
  string,
  (options?: FetchAPIOptions) => Promise<unknown>
>([
  [
    EDITORS_PICK_SOURCE_SLUG,
    options => getEditorsPick({ ...options, refresh: true }),
  ],
  [
    SERIES_EDITORS_PICK_SOURCE_SLUG,
    options => getSeriesEditorsPick({ ...options, refresh: true }),
  ],
]);

/**
 * 등록된 소스 slug 하나의 캐시를 재생성한다.
 *
 * 미등록 slug 는 **조용히 넘기지 않고 실패시킨다** — editors_pick 카테고리에 오타 slug 나
 * 아직 코드에 없는 소스 글을 저장했을 때, no-op 으로 넘기면 "저장했는데 아무 일도 일어나지
 * 않는" 상태가 되고 그게 이 레포가 반복해서 당한 실패 모드다. 에러에 등록된 slug 를 함께
 * 담아 CMS 쪽에서 바로 고칠 수 있게 한다.
 */
export const refreshEditorsPickSource = async (
  slug: string,
  options?: FetchAPIOptions,
): Promise<unknown> => {
  const refresher = EDITORS_PICK_SOURCES.get(slug);
  if (!refresher) {
    throw new Error(
      `등록되지 않은 editors_pick 소스 슬러그입니다: ${slug} ` +
        `(등록된 슬러그: ${Array.from(EDITORS_PICK_SOURCES.keys()).join(', ')})`,
    );
  }

  return refresher(options);
};
